window.HomePage = function HomePage({ 
  setActiveTab, 
  openQuoteModal,
  setSelectedIndustrySlug,
  setSelectedSolutionSlug,
  setSelectedProductSlug,
  setSelectedProductCategory
}) {
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('/api/storage/')) {
      return url;
    }
    if (url.startsWith('/storage/')) {
      return `/api${url}`;
    }
    return url;
  };

  // Hero Slider state
  const [currentSlide, setCurrentSlide] = React.useState(0);
  
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50 && slides.length > 0) {
      if (diff > 0) setCurrentSlide((prev) => (prev + 1) % slides.length);
      else setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  // Dynamic state loaded from CMS with built-in engineering defaults
  const [positioning, setPositioning] = React.useState({
    eyebrow: "",
    h1: "",
    statement: "",
    stats: { years: "", projects: "", sectors: "", systems: "", clients: "" }
  });

  const [slides, setSlides] = React.useState([]);

  const [engineeringProcess, setEngineeringProcess] = React.useState([]);

  const [homeFeatures, setHomeFeatures] = React.useState({
    engineeringScope: [],
    stats: [],
    whyNovatek: []
  });

  const [activeProcessStep, setActiveProcessStep] = React.useState(0);
  const [isProcessPaused, setIsProcessPaused] = React.useState(false);
  const processStepsContainerRef = React.useRef(null);
  const stepItemsRef = React.useRef([]);

  // Clients Data with Contextual Tags
  const [clientsData, setClientsData] = React.useState({ railways: [], automotive: [], rd: [], aerospace: [] });

  // Fetch dynamic content from CMS & setup sync listener
  React.useEffect(() => {
    fetchCmsContent();
    const handleSync = () => fetchCmsContent();
    window.addEventListener('novatek_content_updated', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('novatek_content_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const fetchCmsContent = async () => {
    try {
      const res = await window.apiClient.get('content.php');
      if (res.success && res.data) {
        if (res.data.home_positioning) setPositioning(res.data.home_positioning);
        if (res.data.home_hero && Array.isArray(res.data.home_hero) && res.data.home_hero.length > 0) {
          setSlides(res.data.home_hero);
        }
        if (res.data.home_process && Array.isArray(res.data.home_process) && res.data.home_process.length > 0) {
          setEngineeringProcess(res.data.home_process);
        }
        if (res.data.home_clients && typeof res.data.home_clients === 'object') {
          setClientsData(res.data.home_clients);
        }
        if (res.data.home_features) {
          setHomeFeatures(res.data.home_features);
        }
      }
    } catch (e) {
      console.warn('HomePage CMS sync warning:', e);
    }
  };

  // Auto slide interval for hero (Disabled as per user request to not change automatically)
  React.useEffect(() => {
    // const timer = setInterval(() => {
    //   setCurrentSlide((prev) => (prev + 1) % slides.length);
    // }, 7000);
    // return () => clearInterval(timer);
  }, [slides.length]);

  // Auto progression for engineering process steps (Disabled as per user request to disable auto scrolling/progression)
  React.useEffect(() => {
    // Auto progression disabled
  }, []);



  const categoriesConfig = [
    {
      id: 'railways',
      title: 'RAILWAY SYSTEMS',
      context: 'Railway Test Systems & Brake Validation Rigs',
      clients: clientsData.railways || []
    },
    {
      id: 'automotive',
      title: 'AUTOMOTIVE & EV',
      context: 'Suspension, EOL Testing & Robotic Assembly',
      clients: clientsData.automotive || []
    },
    {
      id: 'rd',
      title: 'R&D & ACADEMIA',
      context: 'National Research Labs & Premier Institutes',
      clients: clientsData.rd || []
    },
    {
      id: 'aerospace',
      title: 'AEROSPACE & DEFENCE',
      context: 'Flight-Grade Test Stands & Aviation GSE',
      clients: clientsData.aerospace || []
    }
  ];

  return (
    <div className="space-y-0 bg-slate-50">
      
      {/* 1. CLARIFIED HOMEPAGE HERO SLIDER */}
      <section 
        className="relative min-h-[640px] sm:min-h-[700px] overflow-hidden bg-slate-950 text-white select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-10000 scale-105"
              style={{ backgroundImage: `url("${resolveImageUrl(slide.bgImage)}")` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/45"></div>
            </div>
            
            <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 flex items-center">
              <div className="max-w-3xl space-y-6">
                
                {/* Eyebrow */}
                <span className="inline-block font-mono text-xs sm:text-sm font-extrabold tracking-widest text-secondary bg-orange-950/70 border border-orange-500/40 px-3.5 py-1.5 rounded-md shadow-sm backdrop-blur-sm">
                  {slide.sector}
                </span>
                
                {/* H1 Headline */}
                <h1 className="font-sans text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-white drop-shadow-lg">
                  {slide.h1}
                </h1>
                
                {/* Core Description */}
                <p className="font-sans text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed drop-shadow-sm">
                  {slide.description}
                </p>



                {/* Redesigned Hero Action Buttons */}
                <div className="flex flex-wrap items-center gap-4 pt-3">
                  <button
                    onClick={() => {
                      const slug = slide.linkSlug || 'solutions';
                      if (slug === 'solutions' || slug === 'products' || slug === 'about' || slug === 'careers' || slug === 'contact') {
                        setActiveTab(slug);
                      } else if (slug === 'railway' || slug === 'automotive' || slug === 'aerospace' || slug === 'rd' || slug === 'industrial') {
                        if (setSelectedIndustrySlug) setSelectedIndustrySlug(slug);
                        setActiveTab('industries');
                      } else {
                        if (setSelectedSolutionSlug) setSelectedSolutionSlug(slug);
                        setActiveTab('solutions');
                      }
                    }}
                    className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-sans font-bold text-sm sm:text-base text-white bg-[#fd761a] hover:bg-[#e05e07] shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer overflow-hidden border border-orange-400/40"
                  >
                    <span className="relative z-10">{slide.primaryCta || "Explore Test Systems"}</span>
                    <span className="material-symbols-outlined text-lg relative z-10 group-hover:translate-x-1 transition-transform">
                      arrow_forward
                    </span>
                  </button>

                  <button
                    onClick={openQuoteModal}
                    className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-xl font-sans font-bold text-sm sm:text-base text-white bg-slate-900/85 hover:bg-slate-800/90 border border-slate-700/80 hover:border-orange-500/50 shadow-lg backdrop-blur-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-lg text-secondary group-hover:rotate-12 transition-transform">
                      engineering
                    </span>
                    <span>{slide.secondaryCta || "Request Consultation"}</span>
                  </button>
                </div>

              </div>
            </div>
          </div>
        ))}

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentSlide ? 'w-12 bg-secondary shadow-lg' : 'w-4 bg-white/40 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 2. "WHAT WE ENGINEER" SECTION (Immediately below Hero) */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden border-b border-slate-200">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-orange-400/5 blur-[120px]"></div>
          <div className="absolute top-[60%] -left-[10%] w-[40%] h-[40%] rounded-full bg-blue-400/5 blur-[100px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-orange-100/80 border border-orange-200 text-orange-700 shadow-sm">
              <span className="material-symbols-outlined text-sm">precision_manufacturing</span>
              <span className="font-mono text-xs font-bold tracking-widest uppercase">
                Core Engineering Scope
              </span>
            </div>
            <h2 className="font-sans text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-orange-400">Engineer</span>
            </h2>
            <p className="font-sans text-slate-600 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
              Custom-built automated testing machinery, robotic workstations, and high-speed telemetry systems designed specifically around client specifications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {homeFeatures.engineeringScope.map((scope, idx) => (
              <div 
                key={scope.id}
                onClick={() => {
                  if (!scope.linkTo) return;
                  const [tab, slug] = scope.linkTo.split(':');
                  if (tab === 'solutions' && setSelectedSolutionSlug) setSelectedSolutionSlug(slug);
                  if (tab === 'industries' && setSelectedIndustrySlug) setSelectedIndustrySlug(slug);
                  if (tab === 'products' && setSelectedProductCategory) setSelectedProductCategory(slug);
                  if (tab === 'product-detail' && setSelectedProductSlug) setSelectedProductSlug(slug);
                  setActiveTab(tab);
                }}
                className="group relative rounded-[2rem] bg-white shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/20 border border-slate-100 hover:border-orange-200 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col h-full transform hover:-translate-y-2"
              >
                {/* Image Section */}
                <div className="relative h-64 w-full overflow-hidden bg-slate-900 shrink-0">
                  {scope.image ? (
                    <img 
                      src={resolveImageUrl(scope.image)} 
                      alt={scope.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-in-out opacity-90 group-hover:opacity-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                      <span className="material-symbols-outlined text-7xl text-slate-700 group-hover:scale-110 transition-transform duration-500">{scope.icon}</span>
                    </div>
                  )}
                  {/* Vibrant Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-5 left-5 h-9 w-9 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center shadow-md">
                    <span className="material-symbols-outlined text-base text-orange-400">{scope.icon}</span>
                  </div>

                  {/* Title overlays on image bottom */}
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-sans text-2xl font-bold text-white group-hover:text-orange-400 transition-colors duration-300 leading-tight">
                      {scope.title}
                    </h3>
                  </div>
                </div>

                {/* Content Block */}
                <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between relative bg-white">
                  <p className="font-sans text-base text-slate-600 leading-relaxed line-clamp-4">
                    {scope.description}
                  </p>
                  
                  {scope.linkText && (
                    <div className="mt-8 flex items-center justify-between text-sm font-bold text-slate-800 group-hover:text-orange-600 transition-colors pt-6 border-t border-slate-100">
                      <span className="uppercase tracking-widest">{scope.linkText}</span>
                      <div className="h-10 w-10 rounded-full bg-slate-50 group-hover:bg-orange-500 flex items-center justify-center transition-colors duration-500 shadow-sm border border-slate-100 group-hover:border-orange-500">
                        <span className="material-symbols-outlined text-slate-400 group-hover:text-white transition-colors duration-300 transform group-hover:translate-x-1">arrow_forward</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 3. MEASURABLE PROOF & CREDIBILITY STRIP */}
      <section className="py-10 bg-slate-900 text-white border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            {homeFeatures.stats.map((stat) => (
              <div key={stat.id} className={`space-y-1 ${stat.spanTwo ? 'col-span-2 md:col-span-1' : ''}`}>
                <span className="font-mono text-3xl sm:text-4xl font-extrabold text-secondary">{stat.number}</span>
                <span className="text-xs font-mono uppercase text-slate-400 block tracking-wider">{stat.label}</span>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* 4. "WHY NOVATEK?" COMMERCIAL BENEFIT SECTION */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
              The Engineering Advantage
            </span>
            <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Why Novatek?
            </h2>
            <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
            <p className="font-sans text-slate-600 text-base">
              We eliminate finger-pointing between separate mechanical, electrical, and software vendors by providing complete turnkey execution under one roof.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {homeFeatures.whyNovatek.map((item, idx) => (
              <div 
                key={item.id}
                className="group relative rounded-[2rem] bg-white shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/20 border border-slate-100 hover:border-orange-200 transition-all duration-500 overflow-hidden flex flex-col h-full transform hover:-translate-y-2"
              >
                {/* Image Section */}
                <div className="relative h-56 w-full overflow-hidden bg-slate-900 shrink-0">
                  {item.image ? (
                    <img 
                      src={resolveImageUrl(item.image)} 
                      alt={item.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-in-out opacity-90 group-hover:opacity-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                      <span className="material-symbols-outlined text-7xl text-slate-700 group-hover:scale-110 transition-transform duration-500">{item.icon}</span>
                    </div>
                  )}
                  {/* Vibrant Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 h-9 w-9 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center shadow-md">
                    <span className="material-symbols-outlined text-base text-orange-400">{item.icon}</span>
                  </div>

                  {/* Title overlays on image bottom */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-sans text-xl font-bold text-white group-hover:text-orange-400 transition-colors duration-300 leading-tight">
                      {item.title}
                    </h3>
                  </div>
                </div>

                {/* Content Block */}
                <div className="p-5 sm:p-6 flex flex-col flex-1 relative bg-white">
                  <p className="font-sans text-sm sm:text-base text-slate-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 5. 8-STAGE ENGINEERING PROCESS */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
              Structured Project Execution
            </span>
            <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              From Engineering Requirement to Production-Ready System
            </h2>
            <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
            <p className="font-sans text-slate-600 text-base">
              A transparent, milestone-driven engineering methodology ensuring zero surprises during FAT and commissioning.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Left: 8 Stages Navigator styled like Careers Live Openings */}
            <div className="lg:col-span-5 w-full bg-white/95 backdrop-blur-md rounded-2xl border-2 p-6 sm:p-7 shadow-2xl space-y-5 shrink-0 animate-fadeIn relative z-10 self-center text-slate-900 animate-pulseGlow">
              <style>{`
                @keyframes pulseGlow {
                  0%, 100% {
                    border-color: #fd761a;
                    box-shadow: 0 0 15px rgba(253, 118, 26, 0.4);
                  }
                  50% {
                    border-color: #ffedd5;
                    box-shadow: 0 0 25px rgba(253, 118, 26, 0.7);
                  }
                }
                .animate-pulseGlow {
                  animation: pulseGlow 2.5s infinite ease-in-out;
                }
              `}</style>

              {/* Scrollable list of stages */}
              <div className="relative overflow-hidden max-h-[360px] border border-slate-100/50 rounded-xl bg-slate-50/50 p-2">
                <div 
                  ref={processStepsContainerRef}
                  onMouseEnter={() => setIsProcessPaused(true)}
                  onMouseLeave={() => setIsProcessPaused(false)}
                  style={{ 
                    animationPlayState: isProcessPaused ? 'paused' : 'running',
                    animationDuration: '24s'
                  }}
                  className="animate-endlessScroll flex flex-col gap-2.5"
                >
                  {[...engineeringProcess, ...engineeringProcess].map((item, idx) => {
                    const originalIdx = idx % engineeringProcess.length;
                    const isActive = activeProcessStep === originalIdx;
                    return (
                      <div 
                        key={`${originalIdx}-${idx}`}
                        ref={(el) => { if (idx < engineeringProcess.length) stepItemsRef.current[idx] = el; }}
                        onClick={() => setActiveProcessStep(originalIdx)}
                        className={`group border p-3.5 rounded-lg cursor-pointer transition-all duration-300 space-y-1.5 shrink-0 shadow-xs ${
                          isActive
                            ? 'bg-orange-50/40 border-secondary/60 shadow-sm'
                            : 'bg-white hover:bg-orange-50/20 border-slate-200 hover:border-secondary/40'
                        }`}
                      >
                        <div className="flex justify-between items-center gap-2">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${
                              isActive ? 'bg-secondary text-white border-secondary' : 'bg-slate-50 text-slate-500 border-slate-200'
                            }`}>
                              Stage {item.step || `0${originalIdx + 1}`}
                            </span>
                            <h4 className={`font-sans text-sm font-bold transition-colors line-clamp-1 ${
                              isActive ? 'text-secondary' : 'text-slate-800 group-hover:text-secondary'
                            }`}>
                              {item.title}
                            </h4>
                          </div>
                          <span className={`material-symbols-outlined text-slate-400 group-hover:text-secondary text-lg transition-transform group-hover:translate-x-0.5 ${
                            isActive ? 'text-secondary translate-x-0.5' : ''
                          }`}>
                            chevron_right
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right: Active Phase Detailed Showcase (7 Cols) */}
            <div className="lg:col-span-7">
              {engineeringProcess[activeProcessStep] && (
                <div className="group bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-950 text-white rounded-[2rem] p-8 sm:p-12 shadow-2xl shadow-slate-900/40 animate-fadeIn relative overflow-hidden border border-slate-800 hover:border-slate-700 transition-colors duration-500">
                  {/* Decorative Orbs */}
                  <div className="absolute -top-[30%] -right-[20%] w-[60%] h-[60%] rounded-full bg-orange-500/10 blur-[80px] pointer-events-none group-hover:bg-orange-500/20 transition-all duration-700"></div>
                  <div className="absolute -bottom-[30%] -left-[20%] w-[60%] h-[60%] rounded-full bg-blue-500/10 blur-[80px] pointer-events-none group-hover:bg-blue-500/20 transition-all duration-700"></div>

                  <div className="relative z-10">
                    <div className="flex items-center gap-6">
                      <span className="font-sans text-7xl sm:text-[6rem] font-black text-transparent bg-clip-text bg-gradient-to-b from-orange-400 to-orange-600 tracking-tighter leading-none drop-shadow-lg">
                        {engineeringProcess[activeProcessStep].step || `0${activeProcessStep + 1}`}
                      </span>
                      <div className="h-16 w-px bg-slate-700/60"></div>
                      <div>
                        <span className="inline-flex items-center gap-2 font-mono text-xs font-bold text-orange-400 bg-orange-400/10 px-3 py-1 rounded-full uppercase tracking-widest mb-3 border border-orange-400/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse"></span>
                          Milestone {activeProcessStep + 1} of {engineeringProcess.length}
                        </span>
                        <h3 className="font-sans text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                          {engineeringProcess[activeProcessStep].title}
                        </h3>
                      </div>
                    </div>

                    <p className="font-sans text-slate-300 text-lg leading-relaxed pt-8 mt-8 border-t border-slate-700/50">
                      {engineeringProcess[activeProcessStep].description}
                    </p>

                    <div className="pt-8 flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => setActiveProcessStep((prev) => (prev - 1 + engineeringProcess.length) % engineeringProcess.length)}
                        className="h-12 w-12 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 transition-all flex items-center justify-center cursor-pointer shadow-sm backdrop-blur-sm"
                        title="Previous Stage"
                      >
                        <span className="material-symbols-outlined text-xl">arrow_back</span>
                      </button>
                      <button
                        onClick={() => setActiveProcessStep((prev) => (prev + 1) % engineeringProcess.length)}
                        className="h-12 px-6 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-bold text-sm font-sans transition-all flex items-center gap-2 cursor-pointer shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 border border-orange-400/50"
                      >
                        <span>Next Stage</span>
                        <span className="material-symbols-outlined text-lg">arrow_forward</span>
                      </button>
                      <button
                        onClick={openQuoteModal}
                        className="h-12 px-6 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 hover:text-white font-bold text-sm font-sans border border-slate-700 hover:border-slate-500 transition-all cursor-pointer backdrop-blur-sm shadow-sm ml-auto"
                      >
                        Request Consultation
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>

        </div>
      </section>

      {/* 6. TRUSTED BY ENGINEERING & TECHNOLOGY ORGANIZATIONS */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="font-mono text-xs font-bold text-secondary tracking-widest uppercase block">
              Proven Track Record
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Trusted by Engineering & Technology Organizations
            </h2>
            <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
            <p className="font-sans text-slate-600 text-base">
              Delivering specialized test benches and automated machinery for industry leaders across four core sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {categoriesConfig.map((cat) => (
              <div 
                key={cat.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col justify-between"
              >
                {/* Header Section */}
                <div className={`text-white p-5 flex items-center gap-4 ${
                  cat.id === 'railways' ? 'bg-gradient-to-r from-[#111827] to-[#1e3a8a]' :
                  cat.id === 'automotive' ? 'bg-gradient-to-r from-[#1c1412] to-[#5a2114]' :
                  cat.id === 'rd' ? 'bg-gradient-to-r from-[#0f1f1c] to-[#044735]' :
                  'bg-gradient-to-r from-[#111329] to-[#26235b]'
                }`}>
                  <div className="h-16 w-16 bg-white rounded-full border-[3px] border-white/10 shadow-lg flex items-center justify-center shrink-0 p-1">
                    <img 
                      src={`/badges/${cat.id}_badge.png`} 
                      alt={`${cat.title} Badge`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="font-sans text-xl font-bold tracking-[0.15em] uppercase text-white drop-shadow-sm">{cat.title}</h3>
                    <p className="font-sans text-sm text-slate-200 tracking-wide mt-0.5">Industry Client Portfolio</p>
                  </div>
                </div>

                {/* Body Section */}
                <div className="p-6 pb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {cat.clients.map((client, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-xl border border-slate-200 flex flex-col items-center justify-between text-center min-h-[105px] hover:shadow-md hover:border-slate-300 transition-all cursor-pointer"
                      >
                        <div className="h-12 w-full flex items-center justify-center p-1">
                          <img 
                            src={window.resolveMediaUrl(client.logo, 'client')} 
                            alt={client.alt || client.name}
                            onError={(e) => window.handleImageError(e, 'client')}
                            className="max-h-full max-w-full object-contain"
                            loading="lazy"
                          />
                        </div>
                        <span className="text-[11px] font-sans font-bold text-slate-800 truncate w-full mt-2">
                          {client.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer Section */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-[11px] font-mono text-slate-500">
                  <span className="uppercase font-semibold">Sector: {cat.title}</span>
                  <button 
                    onClick={() => {
                      if (setSelectedIndustrySlug) setSelectedIndustrySlug(cat.id === 'rd' ? 'rd' : cat.id === 'railways' ? 'railway' : cat.id);
                      setActiveTab('industries');
                    }}
                    className="text-secondary font-bold hover:text-orange-700 flex items-center gap-1 transition-colors"
                  >
                    <span>View Projects</span>
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </button>
                </div>

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 7. BOTTOM CALL TO ACTION BANNER */}
      <section className="py-20 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="font-mono text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase block">
            Start Your Engineering Project
          </span>
          <h2 className="font-serif text-3xl sm:text-5xl font-bold max-w-3xl mx-auto leading-tight">
            Ready to Engineer a Custom Test Rig or Automation System?
          </h2>
          <p className="font-sans text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">
            Partner with Novatek's design bureau to define your test parameters, mechanical architecture, and automated test sequences.
          </p>
          <div className="pt-4 flex justify-center gap-4 flex-wrap">
            <button
              onClick={openQuoteModal}
              className="bg-secondary hover:bg-orange-600 text-white px-10 py-4 rounded-xl font-bold text-base transition-all shadow-xl hover:shadow-orange-500/30 inline-flex items-center gap-2.5 active:scale-95"
            >
              <span className="material-symbols-outlined text-xl">engineering</span>
              <span>Request Engineering Consultation</span>
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};
